/**
 * Contacts API — Supabase PostgREST queries.
 * All contacts live in the database. Do not save copies in localStorage.
 */

import supabase from "../config/supabase";
import { USER_CARD_EMBED, isMissingRelation } from "./users";

const CONTACT_SELECT =
  `id, user_id, contact_id, listing_id, contact_type, name, business_name, location, business_address, phone, email, abn, is_claimed, created_at, contact:users!contact_id(${USER_CARD_EMBED}, businesses(name, abn, business_address, is_claimed), trading_profiles(offer_tags))`;

async function withLiveContactDetails(rows) {
  const ids = [...new Set((rows || []).map((row) => row.contact_id).filter(Boolean))];
  if (!ids.length) return rows;

  const { data, error } = await supabase
    .from("user_profiles")
    .select("id, email, phone")
    .in("id", ids);

  if (error) {
    if (!isMissingRelation(error)) {
      console.error("Contact details lookup failed:", error.message);
    }
    return rows;
  }

  const byId = Object.fromEntries((data || []).map((row) => [row.id, row]));
  return (rows || []).map((row) => {
    const live = byId[row.contact_id];
    if (!live || !row.contact) return row;
    return {
      ...row,
      contact: {
        ...row.contact,
        email: live.email || row.contact.email,
        phone: live.phone || row.contact.phone,
      },
    };
  });
}

const isUniqueViolation = (error) =>
  error?.code === "23505" || /duplicate key/i.test(error?.message || "");

const contactsApi = {
  async list() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    const { data, error } = await supabase
      .from("contacts")
      .select(CONTACT_SELECT)
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (error) return { success: false, error: error.message };
    return { success: true, data: await withLiveContactDetails(data || []) };
  },

  async save({
    contactId = null,
    listingId = null,
    type = "member",
    name = "",
    businessName = "",
    location = "",
    businessAddress = "",
    phone = "",
    email = "",
    abn = "",
    isClaimed = true,
  } = {}) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    const claimed = Boolean(isClaimed && contactId);
    const row = {
      user_id: user.id,
      contact_id: claimed ? contactId : null,
      listing_id: listingId || null,
      contact_type: type,
      name: name || null,
      business_name: businessName || null,
      location: location || null,
      business_address: businessAddress || null,
      phone: phone || null,
      email: email || null,
      abn: abn || null,
      is_claimed: claimed,
    };

    if (!row.contact_id && !row.listing_id) {
      return { success: false, error: "Missing contact or listing" };
    }

    const { data, error } = await supabase
      .from("contacts")
      .insert(row)
      .select("id")
      .single();

    if (error) {
      if (isUniqueViolation(error)) return { success: true, alreadySaved: true };
      return { success: false, error: error.message };
    }
    return { success: true, data };
  },

  async add(contactId) {
    return this.save({ contactId, type: "member", isClaimed: true });
  },

  /**
   * Save a member found by phone lookup, and let them know.
   *
   * No snapshot fields are written on purpose: the joined users row already
   * supplies a live name and location, and the number typed into the lookup is
   * the bare nine digits, which would beat the real one in mapApiContact's
   * `row.phone || c.phone` and leave the call button dialling without a country
   * code.
   */
  async addMember(contactId) {
    const result = await this.add(contactId);
    if (!result.success) return result;

    // Best effort. They're in the rolodex either way, and a courtesy
    // notification failing is not worth reporting the save as failed.
    try {
      await supabase.rpc("notify_contact_added", {
        p_contact_user_id: contactId,
      });
    } catch {
      // Swallowed deliberately — see above.
    }

    return result;
  },

  async isSaved({ contactId = null, listingId = null } = {}) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, saved: false };

    if (listingId) {
      const { data, error } = await supabase
        .from("contacts")
        .select("id")
        .eq("user_id", user.id)
        .eq("listing_id", listingId)
        .maybeSingle();
      if (error) return { success: false, saved: false, error: error.message };
      if (data) return { success: true, saved: true };
    }

    if (contactId) {
      const { data, error } = await supabase
        .from("contacts")
        .select("id")
        .eq("user_id", user.id)
        .eq("contact_id", contactId)
        .maybeSingle();
      if (error) return { success: false, saved: false, error: error.message };
      return { success: true, saved: Boolean(data) };
    }

    return { success: true, saved: false };
  },

  async remove(rowId) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    const { error } = await supabase
      .from("contacts")
      .delete()
      .eq("user_id", user.id)
      .eq("id", rowId);

    if (error) return { success: false, error: error.message };
    return { success: true };
  },
};

export default contactsApi;
